import {
  BlockStack,
  Card,
  Layout,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
} from '@shopify/polaris';
import React from 'react';

// Fallback for the route-level <Suspense> in Router.js.
//
// That boundary used to render `<div></div>` — a zero-height node that got
// replaced by a full page the instant the route chunk landed. Every single
// navigation therefore booked a large layout shift, which is exactly what the
// Built for Shopify CLS budget (< 0.1) measures.
//
// SIZE MATTERS as much as existence: an undersized skeleton is its own CLS
// source. App Bridge v4 sizes the embedded iframe to OUR content height (see
// app.scss's .Polaris-Frame note), so a short skeleton swapping to a tall page
// visibly stretches the whole app inside the admin; and in standalone mode
// (support-login, localhost) the late height growth pops a scrollbar in,
// shrinking the layout width ~15px and shifting every element sideways. So
// this is shaped and sized like the DASHBOARD — the home route and the page
// this fallback shows for most often: status strip, quick-start card, range
// control, stats card, and a tall report section, ~viewport height in total.
// Taller-than-actual is safe (the page shrinks below the fold); shorter is
// what stretches. If the real dashboard gains/loses major sections, resize
// this with it.
//
// Deliberately built from Polaris' own skeleton primitives rather than
// hand-rolled divs: they carry the same spacing tokens as the real Page/Card,
// so the reserved height actually matches what replaces it.
const RouteSkeleton = () => (
  <SkeletonPage primaryAction>
    <Layout>
      {/* Status strip (PrimeMoqStatusBar ≈ 56px) */}
      <Layout.Section>
        <div style={{ minHeight: 56 }} aria-hidden="true">
          <Card>
            <SkeletonBodyText lines={1} />
          </Card>
        </div>
      </Layout.Section>

      {/* Quick-start templates card (≈ 6 template rows) */}
      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={7} />
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* Date-range control row */}
      <Layout.Section>
        <div style={{ minHeight: 36, maxWidth: 220 }} aria-hidden="true">
          <SkeletonDisplayText size="small" />
        </div>
      </Layout.Section>

      {/* Stats card: heading + subtitle + tile row */}
      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={5} />
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* Blocked-value section: tiles + 220px chart + per-rule table */}
      <Layout.Section>
        <Card>
          <BlockStack gap="400">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={4} />
            <div style={{ height: 220 }} aria-hidden="true">
              <SkeletonBodyText lines={2} />
            </div>
            <SkeletonBodyText lines={5} />
          </BlockStack>
        </Card>
      </Layout.Section>
    </Layout>
  </SkeletonPage>
);

// Same idea, for a single lazy card inside an already-rendered page (charts and
// the like). `lines` lets the caller match the real component's height.
export const CardSkeleton = ({ lines = 6 }) => (
  <Card>
    <BlockStack gap="400">
      <SkeletonDisplayText size="small" />
      <SkeletonBodyText lines={lines} />
    </BlockStack>
  </Card>
);

// For a lazy chart: charts have a fixed height, so reserve exactly that rather
// than guessing with text lines.
export const ChartSkeleton = ({ height = 280 }) => (
  <Card>
    <BlockStack gap="400">
      <SkeletonDisplayText size="small" />
      <div style={{ height, width: '100%' }} aria-hidden="true">
        <SkeletonBodyText lines={1} />
      </div>
    </BlockStack>
  </Card>
);

export default RouteSkeleton;
